import { useEffect, useState } from 'preact/hooks';
import type { Deck, Note, Rating } from '../shared/types.ts';
import { cardId, CARD_KINDS } from '../shared/types.ts';
import { openDatabase, getMeta, setMeta, type DB, type LocalDeck, type LocalNote, type LocalReview } from './db.ts';
import { createScheduler, replay, buildQueue, studyDayStart, type CardInfo, type Scheduler, type Queue } from './srs.ts';

/** Per-device preferences. Decks, cards and progress sync; these don't. */
export interface Settings {
  maxReviews: number;
  /** Show the written form on the answer side of audio decks. */
  showText: boolean;
  autoplay: boolean;
  slowRate: number;
  dayStartHour: number;
  /** Deck being studied, or null for all. */
  studyDeck: string | null;
  /** Extra new cards granted by "learn more", for one study day only. */
  extraNew: { day: number; count: number };
}

export const DEFAULT_SETTINGS: Settings = {
  maxReviews: 200,
  showText: false,
  autoplay: true,
  slowRate: 0.7,
  dayStartHour: 4,
  studyDeck: null,
  extraNew: { day: 0, count: 0 },
};

export type SyncStatus = 'idle' | 'syncing' | 'error' | 'offline' | 'signed-out';

export interface SyncState {
  status: SyncStatus;
  lastSyncAt: number | null;
  error: string | null;
  pending: number;
}

function newId(): string {
  return crypto.randomUUID();
}

/**
 * In-memory view of the local database plus the derived card schedules.
 * Views read from it directly and re-render on `version` bumps.
 */
export class Model {
  db!: DB;
  scheduler!: Scheduler;
  decks = new Map<string, LocalDeck>();
  notes = new Map<string, LocalNote>();
  reviewsByCard = new Map<string, LocalReview[]>();
  cards = new Map<string, CardInfo>();
  settings: Settings = DEFAULT_SETTINGS;
  token: string | null = null;
  sync: SyncState = { status: 'idle', lastSyncAt: null, error: null, pending: 0 };
  /** The last answer given on this device, for undo. */
  lastAnswer: { review: LocalReview; cardId: string } | null = null;
  lastCardId: string | null = null;
  ready = false;

  version = 0;
  private listeners = new Set<() => void>();
  onLocalChange: (kind: 'review' | 'note' | 'deck') => void = () => {};

  async load(): Promise<void> {
    this.db = await openDatabase();
    this.scheduler = await createScheduler();
    this.settings = { ...DEFAULT_SETTINGS, ...(await getMeta(this.db, 'settings', {})) };
    this.token = await getMeta<string | null>(this.db, 'token', null);
    this.sync.lastSyncAt = await getMeta<number | null>(this.db, 'lastSyncAt', null);
    for (const d of await this.db.getAll('decks')) this.decks.set(d.id, d);
    for (const n of await this.db.getAll('notes')) this.notes.set(n.id, n);
    for (const r of await this.db.getAll('reviews')) {
      const list = this.reviewsByCard.get(r.cardId) ?? [];
      list.push(r);
      this.reviewsByCard.set(r.cardId, list);
    }
    this.rebuildAll();
    await this.countPending();
    this.ready = true;
    this.changed();
  }

  subscribe(fn: () => void): () => void {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  }

  changed(): void {
    this.version++;
    for (const fn of this.listeners) fn();
  }

  // ---- derived state ----

  rebuildCard(id: string): void {
    this.cards.set(id, replay(this.scheduler, id, this.reviewsByCard.get(id) ?? []));
  }

  rebuildAll(): void {
    this.cards.clear();
    for (const n of this.notes.values()) for (const kind of CARD_KINDS) this.rebuildCard(cardId(n.id, kind));
  }

  queue(deckId: string | null = this.studyDeck(), now = Date.now()): Queue {
    const day = studyDayStart(now, this.settings.dayStartHour);
    return buildQueue(
      this.decks,
      this.notes,
      this.cards,
      {
        maxReviews: this.settings.maxReviews,
        dayStartHour: this.settings.dayStartHour,
        extraNewToday: this.settings.extraNew.day === day ? this.settings.extraNew.count : 0,
        deckId,
      },
      now,
    );
  }

  /** The chosen study deck, if it still exists and is switched on. */
  studyDeck(): string | null {
    const d = this.settings.studyDeck ? this.decks.get(this.settings.studyDeck) : undefined;
    return d && !d.deleted && d.active !== false ? d.id : null;
  }

  /** Decks that are switched on. */
  activeDecks(): LocalDeck[] {
    return this.liveDecks().filter((d) => d.active !== false);
  }

  liveDecks(): LocalDeck[] {
    return [...this.decks.values()].filter((d) => !d.deleted).sort((a, b) => a.position - b.position || a.createdAt - b.createdAt);
  }

  /** Notes in decks that still exist. */
  liveNotes(): LocalNote[] {
    return [...this.notes.values()].filter((n) => !n.deleted && this.decks.get(n.deckId) && !this.decks.get(n.deckId)!.deleted);
  }

  deckOf(note: Note): LocalDeck | undefined {
    return this.decks.get(note.deckId);
  }

  async countPending(): Promise<void> {
    const [d, n, r] = await Promise.all([
      this.db.countFromIndex('decks', 'dirty', 1),
      this.db.countFromIndex('notes', 'dirty', 1),
      this.db.countFromIndex('reviews', 'dirty', 1),
    ]);
    this.sync.pending = d + n + r;
  }

  // ---- actions ----

  /**
   * Synchronous in memory so the caller can pick the next card and start its
   * audio inside the same tap, which mobile Safari requires; the write to
   * IndexedDB follows.
   */
  answer(card: CardInfo, rating: Rating, durationMs: number): void {
    const review: LocalReview = {
      id: newId(),
      cardId: card.id,
      rating,
      reviewedAt: Date.now(),
      durationMs: Math.round(durationMs),
      deleted: false,
      dirty: 1,
    };
    this.db.put('reviews', review).catch((e) => {
      console.error('saving review failed', e);
      this.sync.error = 'Could not save a review on this device.';
      this.changed();
    });
    const list = this.reviewsByCard.get(card.id) ?? [];
    list.push(review);
    this.reviewsByCard.set(card.id, list);
    this.rebuildCard(card.id);
    this.lastAnswer = { review, cardId: card.id };
    this.lastCardId = card.id;
    this.sync.pending++;
    this.changed();
    this.onLocalChange('review');
  }

  /** Takes back the last answer. Works after a sync too: the review is tombstoned, not removed. */
  async undo(): Promise<CardInfo | null> {
    const last = this.lastAnswer;
    if (!last) return null;
    // The answer's own write may still be in flight; this one queues behind it.
    const review: LocalReview = { ...last.review, deleted: true, dirty: 1 };
    await this.db.put('reviews', review);
    const list = this.reviewsByCard.get(last.cardId) ?? [];
    this.reviewsByCard.set(
      last.cardId,
      list.map((r) => (r.id === review.id ? review : r)),
    );
    this.rebuildCard(last.cardId);
    this.lastAnswer = null;
    this.lastCardId = null;
    await this.countPending();
    this.changed();
    this.onLocalChange('review');
    return this.cards.get(last.cardId) ?? null;
  }

  async saveNote(fields: Partial<Note> & { id?: string }): Promise<LocalNote> {
    const now = Date.now();
    const prev = fields.id ? this.notes.get(fields.id) : undefined;
    const note: LocalNote = {
      deckId: this.liveDecks()[0]?.id ?? '',
      text: '',
      reading: '',
      meaning: '',
      notes: '',
      tags: [],
      position: 0,
      recordings: [],
      createdAt: now,
      deleted: false,
      tts: [],
      ...prev,
      ...fields,
      id: prev?.id ?? newId(),
      updatedAt: Math.max(now, (prev?.updatedAt ?? 0) + 1),
      dirty: 1,
    };
    // The server's clips belong to the old text or the old deck's voices; it will send new ones.
    if (prev && (prev.text.trim() !== note.text.trim() || prev.deckId !== note.deckId)) note.tts = [];
    await this.db.put('notes', note);
    this.notes.set(note.id, note);
    if (!prev) for (const kind of CARD_KINDS) this.rebuildCard(cardId(note.id, kind));
    await this.countPending();
    this.changed();
    this.onLocalChange('note');
    return note;
  }

  async saveDeck(fields: Partial<Deck> & { id?: string }): Promise<LocalDeck> {
    const now = Date.now();
    const prev = fields.id ? this.decks.get(fields.id) : undefined;
    const deck: LocalDeck = {
      name: 'New deck',
      language: 'none',
      kinds: { recognize: true, produce: true },
      newPerDay: 10,
      active: true,
      position: Math.max(0, ...this.liveDecks().map((d) => d.position)) + 1,
      createdAt: now,
      deleted: false,
      ...prev,
      ...fields,
      id: prev?.id ?? newId(),
      updatedAt: Math.max(now, (prev?.updatedAt ?? 0) + 1),
      dirty: 1,
    };
    await this.db.put('decks', deck);
    this.decks.set(deck.id, deck);
    if (prev && prev.language !== deck.language) {
      // Clips for the old voices no longer apply; the server sends the new set.
      for (const n of this.notes.values()) if (n.deckId === deck.id) n.tts = [];
    }
    await this.countPending();
    this.changed();
    this.onLocalChange('deck');
    return deck;
  }

  async saveSettings(patch: Partial<Settings>): Promise<void> {
    this.settings = { ...this.settings, ...patch };
    await setMeta(this.db, 'settings', this.settings);
    this.changed();
  }

  async learnMore(n: number): Promise<void> {
    const day = studyDayStart(Date.now(), this.settings.dayStartHour);
    const cur = this.settings.extraNew.day === day ? this.settings.extraNew.count : 0;
    await this.saveSettings({ extraNew: { day, count: cur + n } });
  }

  async setToken(token: string | null): Promise<void> {
    this.token = token;
    await setMeta(this.db, 'token', token);
    this.changed();
  }

  // ---- applying a sync ----

  /** Called by sync with rows it has already written to IndexedDB. */
  applyPulled(decks: LocalDeck[], notes: LocalNote[], reviews: LocalReview[]): void {
    for (const d of decks) this.decks.set(d.id, d);
    const touched = new Set<string>();
    for (const n of notes) {
      const isNew = !this.notes.has(n.id);
      this.notes.set(n.id, n);
      if (isNew) for (const kind of CARD_KINDS) touched.add(cardId(n.id, kind));
    }
    for (const r of reviews) {
      const list = this.reviewsByCard.get(r.cardId) ?? [];
      const i = list.findIndex((x) => x.id === r.id);
      if (i >= 0) list[i] = r;
      else list.push(r);
      this.reviewsByCard.set(r.cardId, list);
      touched.add(r.cardId);
      if (this.lastAnswer?.review.id === r.id) this.lastAnswer.review = r;
    }
    for (const id of touched) this.rebuildCard(id);
  }
}

export const model = new Model();

/** Re-render on any model change. */
export function useModel(): Model {
  const [, setV] = useState(0);
  useEffect(() => model.subscribe(() => setV(model.version)), []);
  return model;
}
